import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import type { StateStorage } from 'zustand/middleware';
import { i18n } from '@/i18n';
import { createIndexedDbStore, type AsyncKeyValueStore } from './indexedDbStore';

/** `recovery`: IndexedDB failed or was too slow to read a state localStorage did not have. */
export type DurableStorageFailure = 'local' | 'mirror' | 'recovery';

export interface DurableStorageOptions {
  /** IndexedDB in the app; null keeps localStorage alone. */
  mirror: AsyncKeyValueStore | null;
  local?: () => Storage | null;
  /** How long hydration waits for IndexedDB before starting empty. */
  recoveryTimeoutMs?: number;
  onFailure?: (key: string, failure: DurableStorageFailure) => void;
}

export interface DurableStorage extends StateStorage {
  /** Resolves once every queued IndexedDB write has landed (or failed), or after `maxWaitMs`. */
  whenMirrored: (maxWaitMs?: number) => Promise<void>;
  /**
   * Called when IndexedDB answers after hydration gave up on it: a `getItem`
   * of that key made during the call returns what it read.
   */
  onLateRecovery: (key: string, listener: () => void) => void;
}

const DEFAULT_RECOVERY_TIMEOUT_MS = 5_000;

type MirrorRead = { ok: true; value: string | null } | { ok: false };

function defaultLocal(): Storage | null {
  return typeof localStorage === 'undefined' ? null : localStorage;
}

/**
 * Storage for the persisted stores that keeps a live match through a full
 * localStorage (VOL-14). localStorage stays the synchronous copy read at
 * start-up, so hydration timing does not change; every write is also mirrored
 * to IndexedDB, which holds far more.
 *
 * Invariant: when localStorage has a value, it is the latest write. A write it
 * refuses (quota) removes its copy, so start-up then reads IndexedDB instead
 * of an older state. Writes never throw: a throwing `setItem` would break the
 * tap that queued the action.
 */
export function createDurableStorage(options: DurableStorageOptions): DurableStorage {
  const { mirror } = options;
  const local = options.local ?? defaultLocal;
  const recoveryTimeoutMs = options.recoveryTimeoutMs ?? DEFAULT_RECOVERY_TIMEOUT_MS;
  const onFailure = options.onFailure ?? (() => {});

  const pending = new Map<string, string | null>();
  /** Keys whose localStorage copy was already mirrored once in this page load. */
  const mirroredAtStart = new Set<string>();
  /**
   * Keys hydrating from IndexedDB. Their in-memory state is not the stored one
   * yet: writing it would replace what is being read back.
   */
  const recovering = new Set<string>();
  /**
   * Keys whose IndexedDB copy could not be read: the store started empty, and
   * mirroring that empty state would erase the copy that may hold the match.
   */
  const preserved = new Set<string>();
  const lateRecovered = new Map<string, string>();
  const lateListeners = new Map<string, () => void>();
  let flushing: Promise<void> | null = null;

  /** Resolves false when a write failed: what is left waits for the next write. */
  const flush = async (): Promise<boolean> => {
    // Writes made in the same tick land in one transaction.
    await Promise.resolve();
    while (mirror && pending.size > 0) {
      const batch = [...pending];
      pending.clear();
      try {
        await mirror.write(batch);
      } catch {
        // Kept for the next write rather than retried in a loop.
        for (const [key, value] of batch) {
          if (!pending.has(key)) pending.set(key, value);
          onFailure(key, 'mirror');
        }
        return false;
      }
    }
    return true;
  };

  const startFlush = (): void => {
    flushing = flush().then((ok) => {
      flushing = null;
      // A write queued after the loop ended but before this point found a flush
      // still running and did not start one.
      if (ok && pending.size > 0) startFlush();
    });
  };

  const scheduleMirror = (key: string, value: string | null): void => {
    if (!mirror || preserved.has(key)) return;
    pending.set(key, value);
    if (!flushing) startFlush();
  };

  const recover = (store: AsyncKeyValueStore, key: string): Promise<string | null> => {
    recovering.add(key);
    const read: Promise<MirrorRead> = store.get(key).then(
      (value) => ({ ok: true, value }),
      () => ({ ok: false }),
    );
    const timeout = new Promise<'timeout'>((resolve) => {
      setTimeout(() => resolve('timeout'), recoveryTimeoutMs);
    });
    return Promise.race([read, timeout]).then((result) => {
      recovering.delete(key);
      if (result !== 'timeout' && result.ok) return result.value;
      preserved.add(key);
      onFailure(key, 'recovery');
      if (result === 'timeout') {
        void read.then((late) => {
          if (!late.ok || !preserved.has(key)) return;
          preserved.delete(key);
          if (late.value === null) return;
          // Served to the `getItem` the listener's rehydration makes right away,
          // dropped if it declines.
          lateRecovered.set(key, late.value);
          lateListeners.get(key)?.();
          lateRecovered.delete(key);
        });
      }
      return null;
    });
  };

  return {
    getItem(key) {
      const late = lateRecovered.get(key);
      if (late !== undefined) {
        lateRecovered.delete(key);
        return late;
      }
      let value: string | null = null;
      try {
        value = local()?.getItem(key) ?? null;
      } catch {
        value = null;
      }
      if (value !== null) {
        // First launch with this storage: the localStorage state is copied over.
        if (!mirroredAtStart.has(key)) {
          mirroredAtStart.add(key);
          scheduleMirror(key, value);
        }
        return value;
      }
      return mirror ? recover(mirror, key) : null;
    },

    setItem(key, value) {
      if (recovering.has(key)) return;
      scheduleMirror(key, value);
      try {
        const storage = local();
        if (!storage) throw new Error('localStorage unavailable');
        storage.setItem(key, value);
      } catch {
        onFailure(key, 'local');
        // Without a mirror write the older copy is still better than none.
        if (mirror && !preserved.has(key)) {
          try {
            local()?.removeItem(key);
          } catch {
            // Nothing left to do: IndexedDB holds the state.
          }
        }
      }
    },

    // A removal is wanted (sign-out): it also drops a copy kept from a failed read.
    removeItem(key) {
      preserved.delete(key);
      lateRecovered.delete(key);
      try {
        local()?.removeItem(key);
      } catch {
        // Removal from IndexedDB below is enough for start-up.
      }
      scheduleMirror(key, null);
    },

    whenMirrored: (maxWaitMs) => {
      const drained = (async () => {
        while (flushing) await flushing;
      })();
      if (maxWaitMs === undefined) return drained;
      return Promise.race([drained, new Promise<void>((resolve) => { setTimeout(resolve, maxWaitMs); })]);
    },

    onLateRecovery(key, listener) {
      lateListeners.set(key, listener);
    },
  };
}

/**
 * Reports each failure once to Sentry, and warns the coach once when a state
 * is kept nowhere any more: localStorage refused it and IndexedDB is missing
 * or failing. The match goes on in memory; a reload would lose it.
 */
export function createFailureReporter(options: {
  mirrorAvailable: boolean;
  report: (key: string, failure: DurableStorageFailure) => void;
  warnNothingKept: () => void;
}): (key: string, failure: DurableStorageFailure) => void {
  const failures = new Map<string, Set<DurableStorageFailure>>();
  let warned = false;
  return (key, failure) => {
    const kinds = failures.get(key) ?? new Set<DurableStorageFailure>();
    if (!kinds.has(failure)) options.report(key, failure);
    kinds.add(failure);
    failures.set(key, kinds);
    // After a failed read the key is not mirrored any more either (`recovery`).
    const nothingKept = kinds.has('local')
      && (!options.mirrorAvailable || kinds.has('mirror') || kinds.has('recovery'));
    if (nothingKept && !warned) {
      warned = true;
      options.warnNothingKept();
    }
  };
}

/** A warning read mid-match: long enough to be seen between two rallies. */
const NOTHING_KEPT_TOAST_MS = 15_000;

const indexedDbMirror = createIndexedDbStore();

/** Shared by the live-capture stores: match state and sync queue. */
export const durableStorage = createDurableStorage({
  mirror: indexedDbMirror,
  onFailure: createFailureReporter({
    mirrorAvailable: indexedDbMirror !== null,
    report: (key, failure) =>
      Sentry.captureMessage('Persisted store write failed', {
        level: failure === 'local' ? 'warning' : 'error',
        extra: { key, failure },
      }),
    warnNothingKept: () =>
      toast.warning(i18n.t('match:sync.storageNotKept'), {
        id: 'storage-not-kept',
        duration: NOTHING_KEPT_TOAST_MS,
      }),
  }),
});

interface PersistedStoreApi {
  setState: (partial: Record<string, never>) => void;
  subscribe: (listener: () => void) => () => void;
  persist: {
    getOptions: () => { name?: string };
    rehydrate: () => Promise<void> | void;
    onFinishHydration: (listener: () => void) => () => void;
  };
}

export interface PersistAfterRecoveryOptions {
  storage?: DurableStorage;
  /**
   * For a store whose merge replaces the in-memory state: a late IndexedDB
   * answer is dropped once the state changed since it started empty, or it
   * would overwrite what was entered meanwhile.
   */
  keepChangesOverLateRecovery?: boolean;
}

/**
 * Writes the state back once it is read from IndexedDB: writes were held while
 * it loaded, and localStorage has no copy until the next one. Only fires for
 * that asynchronous hydration; a synchronous one finished before this call.
 * A late IndexedDB answer is applied through the store's own merge.
 */
export function persistAfterRecovery(store: PersistedStoreApi, options: PersistAfterRecoveryOptions = {}): void {
  const storage = options.storage ?? durableStorage;
  let changedSinceHydration = false;
  store.persist.onFinishHydration(() => {
    store.setState({});
    changedSinceHydration = false;
  });
  store.subscribe(() => {
    changedSinceHydration = true;
  });
  const name = store.persist.getOptions().name;
  if (!name) return;
  storage.onLateRecovery(name, () => {
    if (options.keepChangesOverLateRecovery && changedSinceHydration) {
      Sentry.captureMessage('Late persisted state dropped', { level: 'warning', extra: { key: name } });
      // IndexedDB still holds the older state: the current one replaces it now.
      store.setState({});
      return;
    }
    void store.persist.rehydrate();
  });
}
